import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { useCallback, useMemo } from 'react';
import { MinusIcon } from '../icons/minusIcon';
import { PlusIcon } from '../icons/plusIcon';
import { thunkSetBetSliderAmount } from '../redux/actions/pokerActions';
import { useAppDispatch } from '../redux/store';
import { getDecreasedBetAmount, getIncreasedBetAmount } from '../scenes/poker/controls/betUtils';
import { BET_STEP_DISABLED_BUTTON_ICON_COLOR, GAME_BUTTON_BACKGROUND_COLOR, GAME_BUTTON_BACKGROUND_DISABLED_COLOR, GAME_BUTTON_BACKGROUND_HOVER_COLOR, GAME_BUTTON_BORDER_COLOR } from '../styles/colors';
import { BUTTON_BORDER_WIDTH_PX, BUTTON_HIGHLIGHT_PADDING_PX } from './domBetSlider';
const BUTTON_BASE_CSS = css `
  position: relative;
  width: 50%;
  height: 100%;
  cursor: pointer;
  pointer-events: initial;
  user-select: none;
  border: ${BUTTON_BORDER_WIDTH_PX}px solid ${GAME_BUTTON_BORDER_COLOR};
  background-color: ${GAME_BUTTON_BACKGROUND_COLOR};
  &:hover {
    background-color: ${GAME_BUTTON_BACKGROUND_HOVER_COLOR};
  }
  &:disabled {
    cursor: initial;
    background-color: ${GAME_BUTTON_BACKGROUND_DISABLED_COLOR};
  }
`;
export const DomBetStepButtons = ({ myTurnProperties, bounds }) => {
    const dispatch = useAppDispatch();
    const { betSliderAmount, betSliderMinAmount, betSliderMaxAmount, betIncrement } = myTurnProperties;
    const isDecreaseEnabled = betSliderAmount > betSliderMinAmount;
    const isIncreaseEnabled = betSliderAmount < betSliderMaxAmount;
    const onClickDecrease = useCallback(() => {
        if (!isDecreaseEnabled) {
            return;
        }
        const newValue = getDecreasedBetAmount(betSliderAmount, betSliderMinAmount, betIncrement);
        dispatch(thunkSetBetSliderAmount(newValue));
    }, [betIncrement, betSliderAmount, betSliderMinAmount, dispatch, isDecreaseEnabled]);
    const onClickIncrease = useCallback(() => {
        if (!isIncreaseEnabled) {
            return;
        }
        const newValue = getIncreasedBetAmount(betSliderAmount, betSliderMaxAmount, betIncrement);
        dispatch(thunkSetBetSliderAmount(newValue));
    }, [betIncrement, betSliderAmount, betSliderMaxAmount, dispatch, isIncreaseEnabled]);
    const containerCss = useMemo(() => css `
      position: absolute;
      top: ${bounds.y}px;
      left: ${bounds.x}px;
      width: ${bounds.w}px;
      height: ${bounds.h}px;
    `, [bounds.h, bounds.w, bounds.x, bounds.y]);
    const buttonWidth = bounds.w / 2;
    const buttonBorderRadius = bounds.h / 2;
    const decreaseButtonCss = useMemo(() => css(BUTTON_BASE_CSS, css `
          border-top-left-radius: ${buttonBorderRadius}px;
          border-bottom-left-radius: ${buttonBorderRadius}px;
          border-right-width: ${BUTTON_BORDER_WIDTH_PX / 2}px;
        `), [buttonBorderRadius]);
    const decreaseButtonHighlightCss = useMemo(() => css `
      position: absolute;
      width: ${Math.max(0, buttonWidth - 2 * BUTTON_BORDER_WIDTH_PX - 2 * BUTTON_HIGHLIGHT_PADDING_PX)}px;
      height: ${Math.max(0, bounds.h / 2 - BUTTON_HIGHLIGHT_PADDING_PX)}px;
      left: ${BUTTON_HIGHLIGHT_PADDING_PX}px;
      top: ${BUTTON_HIGHLIGHT_PADDING_PX}px;
      border-top-left-radius: ${Math.max(0, buttonBorderRadius - BUTTON_HIGHLIGHT_PADDING_PX)}px;
      background-color: white;
      opacity: 0.2; // TODO: Use a solid color for the highlight (which would need to be updated on hover)
    `, [bounds.h, buttonBorderRadius, buttonWidth]);
    const increaseButtonCss = useMemo(() => css(BUTTON_BASE_CSS, css `
          border-top-right-radius: ${buttonBorderRadius}px;
          border-bottom-right-radius: ${buttonBorderRadius}px;
          border-left-width: ${BUTTON_BORDER_WIDTH_PX / 2}px;
        `), [buttonBorderRadius]);
    const increaseButtonHighlightCss = useMemo(() => css `
      position: absolute;
      width: ${Math.max(0, buttonWidth - 2 * BUTTON_BORDER_WIDTH_PX - 2 * BUTTON_HIGHLIGHT_PADDING_PX)}px;
      height: ${Math.max(0, bounds.h / 2 - BUTTON_HIGHLIGHT_PADDING_PX)}px;
      left: ${BUTTON_HIGHLIGHT_PADDING_PX}px;
      top: ${BUTTON_HIGHLIGHT_PADDING_PX}px;
      border-top-right-radius: ${Math.max(0, buttonBorderRadius - BUTTON_HIGHLIGHT_PADDING_PX)}px;
      background-color: white;
      opacity: 0.2; // TODO: Use a solid color for the highlight (which would need to be updated on hover)
    `, [bounds.h, buttonBorderRadius, buttonWidth]);
    const iconCss = useMemo(() => {
        // scale to 90% of the button height, but no larger than the icon's deafult size
        const defaultIconSizePx = 24;
        const scale = Math.min(1, (bounds.h * 0.9) / defaultIconSizePx);
        return css `
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%) scale(${scale});
      /* color impacts icon color: */
      color: black;
    `;
    }, [bounds.h]);
    const disabledIconCss = useMemo(() => css(iconCss, css `
          color: ${BET_STEP_DISABLED_BUTTON_ICON_COLOR};
        `), [iconCss]);
    return (_jsxs("div", { css: containerCss, children: [_jsxs("button", { css: decreaseButtonCss, onPointerDown: onClickDecrease, disabled: !isDecreaseEnabled, title: 'Decrease Bet', children: [isDecreaseEnabled && _jsx("div", { css: decreaseButtonHighlightCss }), _jsx("span", { css: isDecreaseEnabled ? iconCss : disabledIconCss, children: _jsx(MinusIcon, {}) })] }), _jsxs("button", { css: increaseButtonCss, onPointerDown: onClickIncrease, disabled: !isIncreaseEnabled, title: 'Increase Bet', children: [isIncreaseEnabled && _jsx("div", { css: increaseButtonHighlightCss }), _jsx("span", { css: isIncreaseEnabled ? iconCss : disabledIconCss, children: _jsx(PlusIcon, {}) })] })] }));
};
